import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dialog } from '@artosera/ui';
import type { TypeRdvEntree } from '@artosera/shared';
import { priseRdvApi, useActionRdv, type TypeRdv } from '../../api/priseRendezVous.js';
import { useEquipe } from '../../api/equipe.js';
import { useShellContext } from '../shell/context.js';
import { TypeFormulaire } from './TypeFormulaire.js';
import { ErreurRdv } from './ErreurRdv.js';
import styles from './RendezVous.module.css';

export function TypesListe({ types }: { types: TypeRdv[] }) {
  const { galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [edition, setEdition] = useState<string | null>(null);
  const [archive, setArchive] = useState<TypeRdv | null>(null);
  const equipe = useEquipe(galerieSlug);
  const type = types.find((v) => v.id === edition);
  const mutation = useActionRdv(galerieSlug, (corps: TypeRdvEntree, appel) =>
    type
      ? priseRdvApi.modifierType(galerieSlug, type.id, { ...corps, version: type.version }, appel)
      : priseRdvApi.creerType(galerieSlug, corps, appel),
  );
  const archiver = useActionRdv(galerieSlug, (type: TypeRdv, appel) =>
    priseRdvApi.archiverType(galerieSlug, type.id, type.version, appel),
  );
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  return (
    <section className={styles.bloc}>
      <div className={styles.entete}>
        <h2>{t('regie:priseRendezVous.types')}</h2>
        <Button
          onClick={() => {
            mutation.reset();
            setEdition('nouveau');
          }}
        >
          {t('regie:priseRendezVous.nouveauType')}
        </Button>
      </div>
      <ErreurRdv erreur={mutation.error ?? archiver.error ?? equipe.error} />
      {edition !== null ? (
        <TypeFormulaire
          key={`${edition}:${type?.version ?? 0}`}
          type={type}
          membres={equipe.data?.membres ?? []}
          pending={mutation.isPending}
          onSauver={(corps) =>
            void mutation
              .mutateAsync(corps)
              .then(() => setEdition(null))
              .catch(() => {})
          }
          onFermer={() => setEdition(null)}
        />
      ) : null}
      <ul className={styles.liste}>
        {types.map((type) => {
          const statut = type.enLigne
            ? 'regie:priseRendezVous.enLigne'
            : 'regie:priseRendezVous.horsLigne';
          return (
            <li key={type.id} className={styles.carte}>
              <h3>{type.titre[langue] || type.titre.fr}</h3>
              <p>
                {type.slug} · {type.dureeMinutes} {t('regie:priseRendezVous.minutes')}
              </p>
              <Badge>{t(type.archiveLe ? 'regie:priseRendezVous.archive' : statut)}</Badge>
              {!type.archiveLe ? (
                <div className={styles.actions}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      mutation.reset();
                      setEdition(type.id);
                    }}
                  >
                    {t('regie:priseRendezVous.modifierType')}
                  </Button>
                  <Button variant="ghost" onClick={() => setArchive(type)}>
                    {t('regie:priseRendezVous.archiver')}
                  </Button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {archive ? (
        <Dialog
          open
          onOpenChange={(v) => {
            if (!v) setArchive(null);
          }}
          title={t('regie:priseRendezVous.archiver')}
          description={t('regie:priseRendezVous.archiveAide')}
          closeLabel={t('commun:actions.fermer')}
          footer={
            <Button
              loading={archiver.isPending}
              onClick={() =>
                void archiver
                  .mutateAsync(archive)
                  .then(() => setArchive(null))
                  .catch(() => setArchive(null))
              }
            >
              {t('regie:priseRendezVous.confirmer')}
            </Button>
          }
        >
          <p>{archive.titre[langue] || archive.titre.fr}</p>
        </Dialog>
      ) : null}
    </section>
  );
}
